import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Mail,
  Pause,
  Phone,
  Plus,
  Power,
  StickyNote,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { AppLayout } from "@/components/layout/AppLayout";
import { EmptyState } from "@/components/shared/EmptyState";
import { ErrorCard } from "@/components/shared/ErrorCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { PermissionGate } from "@/components/rbac/PermissionGate";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useOwner,
  useDeleteOwner,
  useSuspendAllOwnerBusinesses,
  useToggleAllOwnerBusinesses,
} from "@/hooks/useOwners";
import { SubscriptionTab } from "@/pages/businesses/tabs/SubscriptionTab";
import { formatDate } from "@/utils/format";

const InfoRow = ({
  icon: Icon,
  label,
  value,
}: {
  icon: any;
  label: string;
  value?: string | null;
}) => (
  <div className="flex items-start gap-3 text-sm">
    <Icon className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
    <div className="min-w-0">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-foreground truncate">{value || "—"}</div>
    </div>
  </div>
);

export const OwnerShow = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get("tab") ?? "details";
  const { data: owner, isLoading, error, refetch } = useOwner(id);
  const toggleAll = useToggleAllOwnerBusinesses();
  const suspendAll = useSuspendAllOwnerBusinesses();
  const deleteOwner = useDeleteOwner();

  const onDeleteOwner = () => {
    if (!owner) return;
    if (
      !confirm(
        `Are you sure you want to permanently delete owner "${owner.name}" and ALL their businesses, subscriptions, invoices, and data? This action CANNOT be undone.`,
      )
    ) {
      return;
    }
    deleteOwner.mutate(owner.id, {
      onSuccess: () => {
        toast.success(`Owner "${owner.name}" and all details deleted`);
        navigate("/owners");
      },
      onError: (e) => toast.error(e.message),
    });
  };

  const businesses = owner?.businesses ?? [];
  const visibleCount =
    owner?.visibleBusinessesCount ??
    businesses.filter((b) => b.isVisible).length;
  const allOn = businesses.length > 0 && visibleCount === businesses.length;
  const allOff = visibleCount === 0;

  const onToggleAll = (isVisible: boolean) => {
    if (!owner) return;
    toggleAll.mutate(
      { id: owner.id, isVisible },
      {
        onSuccess: (res) =>
          toast.success(
            `${res.affectedBusinessCount} business(es) now ${isVisible ? "visible" : "hidden"}`,
          ),
        onError: (e) => toast.error(e.message),
      },
    );
  };

  return (
    <AppLayout title="Owner detail">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      ) : error ? (
        <ErrorCard
          message={(error as Error).message}
          onRetry={() => refetch()}
        />
      ) : !owner ? (
        <EmptyState title="Owner not found" />
      ) : (
        <>
          <div className="bg-card rounded-card border border-border p-5 mb-4 flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <h2 className="text-xl font-semibold">{owner.name}</h2>
              <p className="text-sm text-muted-foreground">
                Joined {formatDate(owner.createdAt)} · {businesses.length}{" "}
                business(es) · {visibleCount} visible
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                disabled={allOff || toggleAll.isPending}
                onClick={() => onToggleAll(false)}
              >
                <Power className="w-4 h-4 mr-1.5" /> Turn off all
              </Button>
              <Button
                variant="outline"
                disabled={allOn || toggleAll.isPending}
                onClick={() => onToggleAll(true)}
              >
                <Power className="w-4 h-4 mr-1.5" /> Turn on all
              </Button>
              {businesses.length > 1 && (
                <Button
                  variant="destructive"
                  disabled={suspendAll.isPending}
                  onClick={() => {
                    if (
                      !confirm(
                        `Suspend all ${businesses.length} businesses under ${owner.name}?`,
                      )
                    )
                      return;
                    suspendAll.mutate(
                      { id: owner.id },
                      {
                        onSuccess: (res) =>
                          toast.success(
                            `${res.affectedBusinessCount} business(es) suspended`,
                          ),
                        onError: (e) => toast.error(e.message),
                      },
                    );
                  }}
                >
                  <Pause className="w-4 h-4 mr-1.5" /> Suspend all
                </Button>
              )}
              <Link
                to={`/businesses/create?ownerId=${owner.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-btn bg-primary text-primary-foreground hover:opacity-90"
              >
                <Plus className="w-4 h-4" /> Add business
              </Link>
              <PermissionGate module="owners" action="delete">
                <Button
                  variant="destructive"
                  disabled={deleteOwner.isPending}
                  onClick={onDeleteOwner}
                >
                  <Trash2 className="w-4 h-4 mr-1.5" /> Delete Owner
                </Button>
              </PermissionGate>
            </div>
          </div>

          <Tabs defaultValue={defaultTab}>
            <TabsList>
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="subscription">Subscription</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="pt-4 space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="bg-card rounded-card border border-border p-5 space-y-3">
                  <h3 className="font-semibold">Contact</h3>
                  <InfoRow icon={Phone} label="Phone" value={owner.phone} />
                  <InfoRow
                    icon={Phone}
                    label="WhatsApp"
                    value={owner.whatsapp}
                  />
                  <InfoRow icon={Mail} label="Email" value={owner.email} />
                </div>
                <div className="bg-card rounded-card border border-border p-5 space-y-3">
                  <h3 className="font-semibold">Notes</h3>
                  <InfoRow
                    icon={StickyNote}
                    label="Notes"
                    value={owner.notes}
                  />
                </div>
              </div>

              <div className="bg-card rounded-card border border-border">
                <div className="p-4 border-b border-border flex items-center justify-between">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Building2 className="w-4 h-4" /> Businesses (
                    {businesses.length})
                  </h3>
                </div>
                {businesses.length ? (
                  <ul className="divide-y divide-border">
                    {businesses.map((b) => (
                      <li
                        key={b.id}
                        className="p-4 flex items-center justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <Link
                            to={`/businesses/${b.id}`}
                            className="font-medium hover:underline truncate block"
                          >
                            {b.businessName}
                          </Link>
                          <div className="text-xs text-muted-foreground">
                            {b.city} · {b.phone} · Joined{" "}
                            {formatDate(b.createdAt)}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {b.isVisible ? (
                            <span className="text-xs text-green-600 dark:text-green-400">
                              Visible
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              Hidden
                            </span>
                          )}
                          <StatusBadge status={b.status} />
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="p-8">
                    <EmptyState
                      title="No businesses yet"
                      action={
                        <Link
                          to={`/businesses/create?ownerId=${owner.id}`}
                          className="underline"
                        >
                          Add a business under this owner
                        </Link>
                      }
                    />
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="subscription" className="pt-4">
              <SubscriptionTab ownerId={owner.id} />
            </TabsContent>
          </Tabs>
        </>
      )}
    </AppLayout>
  );
};

export default OwnerShow;
